'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useCallback } from 'react'

export type FilterValue = string | number | boolean | string[] | number[] | null | undefined

interface UpdateURLOptions {
    replace?: boolean
    scroll?: boolean
}

export function useURLState() {
    const router = useRouter()
    const pathname = usePathname()

    const buildQueryString = useCallback((filters: Record<string, FilterValue>): string => {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                return
            }

            if (Array.isArray(value)) {
                value.forEach((v) => {
                    if (v !== null && v !== undefined && v !== '') {
                        params.append(key, v.toString())
                    }
                })
            } else {
                params.append(key, value.toString())
            }
        })

        const queryString = params.toString()
        return queryString ? `?${queryString}` : ''
    }, [])

    const updateURL = useCallback(
        (filters: Record<string, FilterValue>, options: UpdateURLOptions = {}) => {
            const { replace = true, scroll = false } = options
            const queryString = buildQueryString(filters)
            const newUrl = `${pathname}${queryString}`

            if (replace) {
                router.replace(newUrl, { scroll })
            } else {
                router.push(newUrl, { scroll })
            }
        },
        [router, pathname, buildQueryString],
    )

    const getQueryString = useCallback(
        (filters: Record<string, FilterValue>): string => {
            return buildQueryString(filters)
        },
        [buildQueryString],
    )

    return {
        updateURL,
        getQueryString,
        pathname,
    }
}
